import { Dialog, Menu } from '@/components/Base/Headless';
import {
  hasPermission,
  isOnCustomRole,
  isPermissionRegistryReady,
} from '@/utils/permissionRegistry';
import Lucide from '@/components/Base/Lucide';
import Tippy from '@/components/Base/Tippy';
import Button from '@/components/Base/Button';
import clsx from 'clsx';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/stores/hooks';
import { RootState } from '@/stores/store';
import {
  AGENTS,
  AgentSummary,
  getAgentImageSrc,
  getAgentLetter,
  setActiveAgent,
  clearActiveAgent,
} from '@/utils/agents';
import { checkSuperOwner, currentRole } from '@/utils/checkAccess';
import { getClientId } from '@/utils/helper';
import AxiosInstance from '@/api/axios';
import {
  selectWorkspaceContext,
  setSettingsView,
  setWorkspaceContext,
} from '@/stores/sideMenuSlice';
import { isBeaconAllowedPath, isBeaconRole } from '@/utils/beaconAccess';
import { menuItems } from '@/utils/map';
import { ROLE } from '@/utils/enum';
import { selectShowGoogleAnalytics } from '@/stores/activeClientSlice';

function AgentHoverCard({
  title,
  enabled,
  helperText,
}: {
  title: string;
  enabled: boolean;
  helperText: string;
}) {
  return (
    <div
      className='absolute left-full top-1/2 -translate-y-1/2 ml-3 z-[70] w-56 pointer-events-none'
      role='tooltip'
    >
      <div className='relative rounded-xl bg-slate-900 text-white shadow-xl shadow-black/30 p-3'>
        <span className='absolute right-full top-1/2 -translate-y-1/2 border-[6px] border-transparent border-r-slate-900' />

        <div className='flex flex-col items-start mb-2'>
          <span
            className={clsx([
              'shrink-0 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full',
              enabled
                ? 'bg-emerald-500/20 text-emerald-300'
                : 'bg-slate-500/20 text-slate-300',
            ])}
          >
            {enabled ? 'Active' : 'Inactive'}
          </span>
        </div>
        <span className='text-sm font-semibold leading-snug'>{title}</span>

        <p className='mt-1.5 text-[11px] leading-snug text-white/60'>
          {helperText}
        </p>
      </div>
    </div>
  );
}

function AgentButton({
  agent,
  enabled,
  onLocked,
  hideTooltip = false,
}: {
  agent: AgentSummary;
  enabled: boolean;
  onLocked: (agent: AgentSummary) => void;
  hideTooltip?: boolean;
}) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [hovered, setHovered] = useState(false);

  const workspaceContext = useAppSelector(selectWorkspaceContext);
  const isActive =
    enabled &&
    workspaceContext.kind === 'agent' &&
    workspaceContext.agentId === agent.id;

  const handleClick = () => {
    if (enabled) {
      setActiveAgent(agent.id);
      dispatch(setWorkspaceContext({ kind: 'agent', agentId: agent.id }));
      dispatch(setSettingsView(false));
      navigate(agent.path);
    } else {
      onLocked(agent);
    }
  };

  return (
    <div
      className='relative flex items-center'
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span
        className={clsx([
          'absolute -left-4 h-6 w-1 rounded-r-full bg-white transition-all duration-200',
          isActive ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0',
        ])}
      />

      <Button
        className={clsx([
          'flex items-center justify-center w-10 h-10 rounded-xl font-semibold text-sm border-none cursor-pointer',
          'transition-all duration-150',
          isActive
            ? ['text-slate-800 bg-white shadow-lg shadow-black/20 scale-105']
            : [agent.colorClass, 'text-white hover:scale-105'],
          !enabled && 'opacity-50',
        ])}
        onClick={handleClick}
      >
        {getAgentLetter(agent)}
      </Button>

      {!hideTooltip && hovered && (
        <AgentHoverCard
          title={agent.tooltipTitle}
          enabled={enabled}
          helperText='Click to view performance & insights'
        />
      )}
    </div>
  );
}

function AgentAvatar({
  agent,
  className,
}: {
  agent: AgentSummary;
  className?: string;
}) {
  const [imageFailed, setImageFailed] = useState(false);

  if (imageFailed) {
    return (
      <div
        className={clsx([
          'flex items-center justify-center font-semibold text-white h-12 w-12',
          agent.colorClass,
          className,
        ])}
      >
        {getAgentLetter(agent)}
      </div>
    );
  }

  return (
    <img
      src={getAgentImageSrc(agent)}
      alt={agent.name}
      onError={() => setImageFailed(true)}
      className={clsx(['object-cover h-12', className])}
    />
  );
}

function AgentUpgradeDialog({
  agent,
  onClose,
}: {
  agent: AgentSummary | null;
  onClose: () => void;
}) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!agent) return null;

  const clientId = getClientId();

  const handleContactCsm = async () => {
    setSending(true);
    setError(null);
    try {
      await AxiosInstance.post(`/clients/${clientId}/request-agent`, {
        agentName: agent.name,
      });
      setSent(true);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          'Something went wrong. Please try again.'
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={!!agent} onClose={onClose} size='xl'>
      <Dialog.Panel>
        <Dialog.Title className='flex flex-col gap-3 pt-12'>
          <AgentAvatar
            agent={agent}
            className='rounded-2xl text-xl font-bold'
          />
          <span className='flex flex-wrap items-center gap-6 text-xl md:text-3xl'>
            {agent.name}
          </span>
          <span className='text-sm text-slate-700/80'>
            {agent.role} · {agent.tagline}
          </span>
        </Dialog.Title>

        <Dialog.Description className='flex flex-col gap-4 w-full'>
          <p className='text-sm leading-relaxed text-slate-600'>
            {agent.description}
          </p>

          <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
            {agent.features.map((feature) => (
              <div key={feature.title} className='rounded-lg bg-slate-50 p-3'>
                <div className='flex items-start gap-2'>
                  <span className='mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-600'>
                    <Lucide icon='Check' className='h-2.5 w-2.5' />
                  </span>
                  <div>
                    <div className='text-sm font-medium text-slate-800'>
                      {feature.title}
                    </div>
                    <div className='text-xs text-slate-500'>
                      {feature.description}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className='flex items-start gap-3 rounded-lg border border-indigo-100 bg-indigo-50 p-4'>
            <Lucide
              icon='Info'
              className='mt-0.5 h-4 w-4 shrink-0 text-indigo-500'
            />
            <p className='text-sm text-slate-600'>
              {sent
                ? `Thanks — we've let your Customer Success Manager know you're interested in ${agent.name}. They'll be in touch shortly.`
                : ``}
            </p>
          </div>

          {error && <p className='text-sm text-red-500'>{error}</p>}
        </Dialog.Description>

        <Dialog.Footer className='flex items-center justify-between'>
          <div className='mr-auto text-xs text-slate-400'>
            {sent ? 'Request sent' : 'Sends a request to your CSM'}
          </div>
          <Button
            type='button'
            variant='outline-secondary'
            onClick={onClose}
            className='mr-2 flex items-center gap-2'
          >
            <span>{sent ? 'Close' : 'Maybe later'}</span>
          </Button>
          {!sent && (
            <Button
              type='button'
              variant='primary'
              className='flex items-center gap-2'
              disabled={sending}
              onClick={handleContactCsm}
            >
              <Lucide icon='Mail' className='w-4 h-4' />
              <span>{sending ? 'Sending...' : 'Contact my CSM'}</span>
            </Button>
          )}
        </Dialog.Footer>
      </Dialog.Panel>
    </Dialog>
  );
}

function OrchestrateGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox='0 0 18 18'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.4'
      strokeLinecap='round'
      className={className}
    >
      <circle cx='9' cy='9' r='2.2' />
      <circle cx='3.3' cy='4' r='1.3' />
      <circle cx='14.7' cy='5' r='1.3' />
      <circle cx='4' cy='15' r='1.3' />
      <circle cx='14.4' cy='14' r='1.3' />
      <line x1='7.3' y1='7.6' x2='4.2' y2='4.7' />
      <line x1='10.7' y1='7.8' x2='13.7' y2='5.7' />
      <line x1='7.5' y1='10.6' x2='4.7' y2='13.8' />
      <line x1='10.7' y1='10.4' x2='13.6' y2='13.2' />
    </svg>
  );
}

function OrchestrateButton() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Highlights whenever the user is in Intelligence/chat mode, regardless
  // of which sub-page of it they're on — driven purely by workspaceContext,
  // not the pathname, so it stays correct across menuItems navigation too.
  const workspaceContext = useAppSelector(selectWorkspaceContext);
  const isActive = workspaceContext.kind === 'intelligence';

  return (
    <Tippy as='button' content='Pulse'>
      <div className='relative flex items-center'>
        <span
          className={clsx([
            'absolute -left-4 h-6 w-1 rounded-r-full bg-white transition-all duration-200',
            isActive ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0',
          ])}
        />
        <Button
          className={clsx([
            'flex items-center justify-center w-10 h-10 rounded-xl border-none cursor-pointer',
            'transition-all duration-150',
            isActive
              ? 'text-slate-800 bg-white shadow-lg shadow-black/20 scale-105'
              : 'text-white/80 bg-white/10 hover:bg-white/15 hover:scale-105',
          ])}
          onClick={() => {
            clearActiveAgent();
            dispatch(setWorkspaceContext({ kind: 'intelligence' }));
            dispatch(setSettingsView(false));
            navigate('/');
          }}
        >
          <OrchestrateGlyph className='w-5 h-5' />
        </Button>
      </div>
    </Tippy>
  );
}

function AgentRailSettingsButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const showGoogleAnalytics = useAppSelector(selectShowGoogleAnalytics);

  const role = currentRole();
  const { user } = useAppSelector((state: RootState) => state.users);
  const activeClient = useAppSelector((state: RootState) => state.activeClient);
  const clientName = activeClient.name;
  const currentLast = location.pathname.replace(/\/$/, '').split('/').pop();

  const go = (pathname: string) => {
    navigate(pathname);
  };

  return (
    <Menu>
      <Tippy as='div' content='Settings & Account'>
        <Menu.Button
          className={clsx([
            'flex items-center justify-center w-10 h-10 rounded-xl border-none cursor-pointer',
            'transition-all duration-150',
            'text-white bg-white/15 ring-1 ring-inset ring-white/25 hover:bg-white/25 hover:scale-105 shadow-sm',
          ])}
        >
          <Lucide icon='Settings' className='w-5 h-5' />
        </Menu.Button>
      </Tippy>

      <Menu.Items className='w-72 mt-1 px-4' placement='top-start'>
        <div>
          <div className='p-2 font-semibold'>{user?.data?.user.name}</div>
          {clientName && (
            <>
              <div className='px-2 mb-2 opacity-80 text-[13px]' role='none'>
                {clientName}
              </div>
              <div
                className='h-px my-2 -mx-2 bg-slate-200/60 dark:bg-darkmode-400'
                role='none'
              ></div>
            </>
          )}
        </div>
        <ul className='relative'>
          {menuItems
            .filter((item) => {
              if (item.title === 'Google Analytics' && !showGoogleAnalytics)
                return false;
              // Beacon is confined to its own area; PrivateRoute would bounce
              // these anyway, so do not offer them.
              if (isBeaconRole(role) && !isBeaconAllowedPath(item.pathname))
                return false;
              // A custom role is judged on the permission the page needs.
              // Entries without one (Help & Resources) stay open to everyone.
              if (isPermissionRegistryReady() && isOnCustomRole()) {
                return item.permissionKey
                  ? hasPermission(item.permissionKey)
                  : true;
              }
              if (!item.permission || item.permission.length === 0) return true;
              return role ? item.permission.includes(role as ROLE) : false;
            })
            .map((item, index) => {
              const itemLast = item.pathname
                .replace(/\/$/, '')
                .split('/')
                .pop();
              const isActive = !!itemLast && currentLast === itemLast;
              return (
                <li key={index}>
                  <Menu.Item
                    as='a'
                    onClick={(event: React.MouseEvent) => {
                      event.preventDefault();
                      go(item.pathname);
                    }}
                    className={clsx([
                      'flex items-center rounded-[0.6rem] border border-transparent px-3.5 py-3 mb-1 text-sm',
                      'transition-all duration-200 ease-in',
                      isActive
                        ? 'bg-white border-slate-200 shadow-[0px_2px_3px_#0000000b] dark:bg-darkmode-400 dark:border-darkmode-300'
                        : 'text-slate-600 hover:bg-white/80 hover:border-slate-200/80 hover:shadow-[0px_2px_3px_#0000000b] dark:text-slate-300 dark:hover:bg-darkmode-400/80',
                    ])}
                  >
                    <div
                      className={clsx([
                        'overflow-hidden text-ellipsis whitespace-nowrap',
                        isActive
                          ? 'text-theme-1 font-medium'
                          : 'text-slate-600 dark:text-slate-300',
                      ])}
                    >
                      {item.title}
                    </div>
                  </Menu.Item>
                </li>
              );
            })}
        </ul>
      </Menu.Items>
    </Menu>
  );
}

interface AgentRailProps {
  userInitial?: string;
  onAddAgent?: () => void;
  mobileOpen?: boolean;
}

function AgentRail({ mobileOpen = false }: AgentRailProps) {
  const agentsMap = useAppSelector(
    (state: RootState) =>
      (state.activeClient as { agents?: Record<string, boolean> | null }).agents
  );
  const isEnabled = (agentId: string) =>
    agentsMap == null ? true : agentsMap[agentId] !== false;

  const [lockedAgent, setLockedAgent] = useState<AgentSummary | null>(null);
  const isSuperOwner = checkSuperOwner();
  return (
    <>
      <div
        className={clsx([
          'sticky top-0 h-screen shrink-0 z-[60] w-[68px] flex-col items-center py-4 gap-3',
          'bg-gradient-to-b from-theme-1 to-theme-2 border-transparent',
          mobileOpen ? 'flex' : 'hidden',
          'xl:flex',
        ])}
      >
        {/* Logo / home — the one full-size circular mark on the rail */}
        <div className='w-12 h-12 rounded-full flex items-center justify-center border-none cursor-pointer p-2 hover:bg-white/10 transition-colors'>
          <img
            src='/favicon.png'
            alt='Keepme'
            className='w-full h-full object-contain'
          />
        </div>

        <OrchestrateButton />

        <div className='w-8 h-px bg-white/10 my-1' />

        <div className='flex flex-col gap-3'>
          {AGENTS.map((agent) => (
            <AgentButton
              key={agent.id}
              agent={agent}
              enabled={isEnabled(agent.id)}
              onLocked={setLockedAgent}
            />
          ))}
        </div>

        <div className='flex-1' />

        <div className='w-8 h-px bg-white/10' />
        <AgentRailSettingsButton />
      </div>

      <AgentUpgradeDialog
        key={lockedAgent?.id ?? 'none'}
        agent={lockedAgent}
        onClose={() => setLockedAgent(null)}
      />
    </>
  );
}

export default AgentRail;
